/**
 * ApplicationCreationWizard
 *
 * Unified 3-step wizard for creating a Forge application.
 *
 * Two entry points share this wizard:
 *  - Cadrage page → "Push to Forge" (pre-filled, locked overview, bound to a use case)
 *  - Forge dashboard → "New application" (editable overview, standalone)
 *
 * Steps:
 *   1. Overview  – name, client, forge, description
 *   2. Template  – grid of templates from /forge/templates
 *   3. Summary   – review + create
 */
import { ArrowLeft, ArrowRight, Box, Check, Layers, Loader2, Rocket, Server, Globe } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import forgeService from '../../services/forgeService';
import notify from '../../lib/notify';
import { extractApiErrorMessage } from '../../lib/apiError';
import { Button } from '../Common/button';
import Modal from '../Common/Modal';
import { cn } from '@/lib/utils';
import { ProgressStep, ProgressStepper } from '../Common/ProgressStepper';
import type {
  CreateStandaloneAppRequest,
  CustomAppTemplateInfo,
  Forge,
  ForgeApplication,
  ForgeClient,
} from '../../types';

export type ApplicationCreationMode =
  | {
      kind: 'use_case';
      useCaseId: string;
      useCaseName: string;
      /** Forge that backs this use case's domain. The wizard resolves the
       *  forge + client labels from this id. */
      forgeId: string;
      /** Cadrage description, used as the default description. */
      description?: string | null;
    }
  | {
      kind: 'standalone';
      clients: ForgeClient[];
      forges: Forge[];
    };

interface ApplicationCreationWizardProps {
  open: boolean;
  onClose: () => void;
  mode: ApplicationCreationMode;
  currentUserEmail: string;
  /** Called once the application has been created in Forge. */
  onCreated: (app: { id: string; name: string }) => void;
}

type Overview = {
  name: string;
  description: string;
  client_id: string;
  forge_id: string;
};

const slugify = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 63);

const NAME_PATTERN = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;

const NAME_MAX = 46;

/**
 * Forge enforces app-name uniqueness per forge. When the chosen name is already
 * taken we propose the next free `<stem>-<n>` variant, trimming the stem so the
 * result still respects the 46-char cap. Returns the original name if it's free.
 */
const suggestUniqueName = (base: string, taken: Set<string>): string => {
  if (!taken.has(base.toLowerCase())) return base;
  const stem = base.replace(/-\d+$/, '');
  for (let i = 2; i < 1000; i += 1) {
    const suffix = `-${i}`;
    const candidate = `${stem.slice(0, NAME_MAX - suffix.length)}${suffix}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
  return base;
};

export const ApplicationCreationWizard: React.FC<ApplicationCreationWizardProps> = ({
  open,
  onClose,
  mode,
  currentUserEmail,
  onCreated,
}) => {
  const { t } = useTranslation();

  // -----------------------------------------------------------------------
  // Initial state
  // -----------------------------------------------------------------------
  const initialOverview = useMemo<Overview>(() => {
    if (mode.kind === 'use_case') {
      return {
        name: slugify(mode.useCaseName),
        description: mode.description?.trim() || `Application for use case: ${mode.useCaseName}`,
        client_id: '',
        forge_id: mode.forgeId,
      };
    }
    return { name: '', description: '', client_id: '', forge_id: '' };
  }, [mode]);

  const [step, setStep] = useState<0 | 1 | 2 | 3>(0);
  const [overview, setOverview] = useState<Overview>(initialOverview);
  const [templates, setTemplates] = useState<CustomAppTemplateInfo[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<CustomAppTemplateInfo | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [createProgressStep, setCreateProgressStep] = useState<number>(-1);
  const [createProgressError, setCreateProgressError] = useState(false);

  // Existing app names in the target forge, lowercased. Used to flag name
  // collisions (Forge enforces uniqueness per forge) before the slow create
  // call and to propose a free alternative.
  const [existingNames, setExistingNames] = useState<Set<string>>(new Set());
  const [namesLoaded, setNamesLoaded] = useState(false);

  // Resolved labels for the locked use_case mode (lazy fetched).
  const [resolvedForge, setResolvedForge] = useState<Forge | null>(null);
  const [resolvedClient, setResolvedClient] = useState<ForgeClient | null>(null);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  // Reset whenever the wizard is (re)opened
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setOverview(initialOverview);
    setSelectedTemplate(null);
    setCreateProgressStep(-1);
    setCreateProgressError(false);
    setResolvedForge(null);
    setResolvedClient(null);
    setResolveError(null);
    setExistingNames(new Set());
    setNamesLoaded(false);
  }, [open, initialOverview]);

  // Load existing app names for the target forge so we can detect collisions
  // and suggest a free name. Refetches whenever the target forge changes.
  useEffect(() => {
    if (!open) return;
    const forgeId = overview.forge_id;
    if (!forgeId) {
      setExistingNames(new Set());
      setNamesLoaded(false);
      return;
    }
    let cancelled = false;
    setNamesLoaded(false);
    forgeService
      .listApplications(forgeId)
      .then((apps) => {
        if (cancelled) return;
        setExistingNames(new Set(apps.map((a) => a.name.toLowerCase())));
        setNamesLoaded(true);
      })
      .catch(() => {
        // Best-effort: if we can't list apps we fall back to the server-side
        // 409, so just treat the name as free.
        if (cancelled) return;
        setExistingNames(new Set());
        setNamesLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, overview.forge_id]);

  // In use_case mode, resolve forge + client to display friendly labels and
  // populate the (read-only) client_id in the overview state. Tries getForge
  // first; falls back to listForges() for users without direct forge access.
  useEffect(() => {
    if (!open || mode.kind !== 'use_case') return;
    let cancelled = false;
    setResolving(true);
    setResolveError(null);

    const resolveForge = async (): Promise<Forge | null> => {
      try {
        return await forgeService.getForge(mode.forgeId);
      } catch {
        try {
          const forges = await forgeService.listForges();
          return forges.find((f) => f.forge_id === mode.forgeId) || null;
        } catch {
          return null;
        }
      }
    };

    resolveForge()
      .then(async (forge) => {
        if (cancelled) return;
        if (!forge) {
          setResolveError(
            t('forge.wizard.resolveError', 'Could not load forge details'),
          );
          return;
        }
        setResolvedForge(forge);
        setOverview((prev) => ({ ...prev, client_id: forge.client_id }));
        try {
          const client = await forgeService.getClient(forge.client_id);
          if (!cancelled) setResolvedClient(client);
        } catch {
          /* client lookup is best-effort */
        }
      })
      .finally(() => {
        if (!cancelled) setResolving(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, mode, t]);

  // Fetch templates on first open
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setTemplatesLoading(true);
    setTemplatesError(null);
    forgeService
      .listAppTemplates()
      .then((list) => {
        if (cancelled) return;
        setTemplates(list);
      })
      .catch((err) => {
        if (cancelled) return;
        setTemplatesError(extractApiErrorMessage(err, t('forge.wizard.templatesError', 'Failed to load templates')));
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, t]);

  // -----------------------------------------------------------------------
  // Derived (admin/standalone mode)
  // -----------------------------------------------------------------------
  const forgesForSelectedClient = useMemo(() => {
    if (mode.kind !== 'standalone') return [];
    return mode.forges.filter((f) => f.client_id === overview.client_id);
  }, [mode, overview.client_id]);

  const clientById = useMemo(() => {
    if (mode.kind !== 'standalone') return new Map<string, ForgeClient>();
    return new Map(mode.clients.map((c) => [c.client_id, c]));
  }, [mode]);

  const forgeById = useMemo(() => {
    if (mode.kind !== 'standalone') return new Map<string, Forge>();
    return new Map(mode.forges.map((f) => [f.forge_id, f]));
  }, [mode]);

  // -----------------------------------------------------------------------
  // Validation
  // -----------------------------------------------------------------------
  // Cap at 46 — Forge truncates the app name with k8s_safe_name() before using
  // it as a Kubernetes label. Names 47–63 chars previously passed validation
  // but got silently truncated server-side, which broke the Logs tab (label
  // mismatch between the full DB name and the truncated pod label).
  const nameValid =
    overview.name.length >= 2 &&
    overview.name.length <= 46 &&
    NAME_PATTERN.test(overview.name);

  // In use_case mode the client_id/forge_id are pre-bound (forge_id provided,
  // client_id resolved lazily by the server). In standalone mode the user
  // must pick both explicitly.
  const targetValid =
    mode.kind === 'use_case'
      ? !!overview.forge_id
      : !!overview.client_id && !!overview.forge_id;

  // Only trust the collision check once names have loaded for the current forge.
  const nameTaken =
    namesLoaded && nameValid && existingNames.has(overview.name.toLowerCase());

  const suggestedName = useMemo(
    () => (nameTaken ? suggestUniqueName(overview.name, existingNames) : ''),
    [nameTaken, overview.name, existingNames],
  );

  const overviewValid = nameValid && targetValid && !nameTaken;

  const canSubmit = overviewValid && !!selectedTemplate && !submitting;

  const createSteps: ProgressStep[] = [
    { id: 'queue', label: t('forge.progress.queuing', 'Queuing') },
    { id: 'build', label: t('forge.progress.building', 'Building') },
    { id: 'finalize', label: t('forge.progress.finalizing', 'Finalizing') },
    { id: 'done', label: t('forge.progress.done', 'Done') },
  ];

  const simulateCreateProgress = async (): Promise<void> => {
    setCreateProgressError(false);
    setCreateProgressStep(0);

    // Small, deterministic animation to reassure users while Forge provisions.
    for (let i = 1; i < createSteps.length - 1; i += 1) {
      await new Promise((resolve) => setTimeout(resolve, 650));
      setCreateProgressStep(i);
    }
  };

  // -----------------------------------------------------------------------
  // Resolve display labels for client/forge in summary
  // -----------------------------------------------------------------------
  const resolvedClientName =
    mode.kind === 'use_case'
      ? resolvedClient?.name || ''
      : clientById.get(overview.client_id)?.name || overview.client_id;

  const resolvedForgeName =
    mode.kind === 'use_case'
      ? resolvedForge?.display_name || resolvedForge?.name || ''
      : (() => {
          const f = forgeById.get(overview.forge_id);
          return f?.display_name || f?.name || overview.forge_id;
        })();

  // -----------------------------------------------------------------------
  // Submit
  // -----------------------------------------------------------------------
  const handleCreate = async () => {
    if (!canSubmit || !selectedTemplate) return;
    setSubmitting(true);
    setCreateProgressError(false);
    setStep(3);
    try {
      let createdId: string;
      let createdName: string;

      const createRequest = async () => {
        if (mode.kind === 'use_case') {
          const result = await forgeService.createApplicationForUseCase(mode.useCaseId, {
            name: overview.name,
            app_type: selectedTemplate.app_type,
            template_name: selectedTemplate.template_name,
            created_by: currentUserEmail,
            description: overview.description || undefined,
          });
          createdId = result.forge_application_id;
          createdName = overview.name;
          return;
        }
        const payload: CreateStandaloneAppRequest = {
          client_id: overview.client_id,
          forge_id: overview.forge_id,
          name: overview.name,
          app_type: selectedTemplate.app_type,
          template_name: selectedTemplate.template_name,
          created_by: currentUserEmail,
          description: overview.description || null,
        };
        const created: ForgeApplication = await forgeService.createStandaloneApplication(payload);
        createdId = created.app_id;
        createdName = created.name;
      };

      await Promise.all([createRequest(), simulateCreateProgress()]);
      setCreateProgressStep(createSteps.length - 1);
      await new Promise((resolve) => setTimeout(resolve, 450));

      notify.show({
        variant: 'success',
        title: t('forge.wizard.createSuccess', 'Application created'),
        description: `"${createdName}" ${t('forge.wizard.createSuccessSuffix', 'is being deployed.')}`,
        link: `/applications/${createdId}`,
      });
      onCreated({ id: createdId, name: createdName });
      onClose();
    } catch (err) {
      setCreateProgressError(true);
      setStep(2);
      notify.error(extractApiErrorMessage(err, t('forge.wizard.createError', 'Failed to create application')));
    } finally {
      setSubmitting(false);
    }
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  if (!open) return null;

  const stepsLabels = [
    t('forge.wizard.steps.overview', 'Overview'),
    t('forge.wizard.steps.template', 'Template'),
    t('forge.wizard.steps.summary', 'Summary'),
    t('forge.wizard.steps.provisioning', 'Provisioning'),
  ];

  const inputCls =
    'w-full px-3 py-2 rounded-md border border-input bg-background text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 disabled:cursor-not-allowed';

  const lockedOverview = mode.kind === 'use_case';

  return (
    <Modal
      open={open}
      onClose={submitting ? () => {} : onClose}
      hideDefaultFooter
      className="max-w-3xl"
      title={t('forge.wizard.title', 'Create application')}
      description={
        lockedOverview
          ? t('forge.wizard.descriptionUseCase', 'Push this scoping to Forge as a new application.')
          : t('forge.wizard.descriptionStandalone', 'Create an application directly in Forge.')
      }
      bodyClassName="max-h-[75vh] overflow-y-auto"
      footer={
        <>
          {step > 0 ? (
            <Button
              variant="outline"
              onClick={() => setStep((s) => (s - 1) as 0 | 1 | 2 | 3)}
              disabled={submitting || step === 3}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('global.back', 'Back')}
            </Button>
          ) : (
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              {t('global.cancel', 'Cancel')}
            </Button>
          )}
          {step < 2 ? (
            <Button
              onClick={() => setStep((s) => (s + 1) as 0 | 1 | 2 | 3)}
              disabled={
                (step === 0 && (!overviewValid || resolving)) ||
                (step === 1 && !selectedTemplate)
              }
              className="gap-2"
            >
              {t('global.next', 'Next')}
              <ArrowRight className="h-4 w-4" />
            </Button>
          ) : step === 2 ? (
            <Button onClick={handleCreate} disabled={!canSubmit} className="gap-2">
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Rocket className="h-4 w-4" />
              )}
              {submitting ? t('global.creating', 'Creating…') : t('forge.wizard.create', 'Create application')}
            </Button>
          ) : (
            <Button disabled className="gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('forge.wizard.provisioning', 'Provisioning…')}
            </Button>
          )}
        </>
      }
    >
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6">
        {stepsLabels.map((label, idx) => {
          const isActive = idx === step;
          const isDone = idx < step;
          return (
            <React.Fragment key={label}>
              <div className="flex items-center gap-2">
                <div
                  className={cn(
                    'h-7 w-7 rounded-full flex items-center justify-center border-2 text-xs font-medium transition-colors',
                    isDone && 'bg-primary border-primary text-primary-foreground',
                    isActive && 'border-primary bg-primary/10 text-foreground',
                    !isDone && !isActive && 'border-border bg-muted/30 text-muted-foreground',
                  )}
                >
                  {isDone ? <Check className="h-3.5 w-3.5" /> : idx + 1}
                </div>
                <span
                  className={cn(
                    'text-sm font-medium',
                    isActive && 'text-foreground',
                    !isActive && 'text-muted-foreground',
                  )}
                >
                  {label}
                </span>
              </div>
              {idx < stepsLabels.length - 1 && (
                <div className={cn('flex-1 h-0.5 mx-1', isDone ? 'bg-primary' : 'bg-border')} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step content */}
      {step === 0 && (
        <div className="space-y-4">
          {resolveError && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              {resolveError}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('forge.wizard.client', 'Client')}
              </label>
              {lockedOverview ? (
                <div className={cn(inputCls, 'flex items-center gap-2 text-foreground/80')}>
                  {resolving && !resolvedClient ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                      <span className="text-muted-foreground">
                        {t('forge.wizard.loading', 'Loading…')}
                      </span>
                    </>
                  ) : (
                    resolvedClientName || '-'
                  )}
                </div>
              ) : (
                <select
                  className={inputCls}
                  value={overview.client_id}
                  onChange={(e) =>
                    setOverview({ ...overview, client_id: e.target.value, forge_id: '' })
                  }
                >
                  <option value="">
                    {t('forgeDashboard.forges.selectClient', '-- Select a client --')}
                  </option>
                  {mode.kind === 'standalone' &&
                    mode.clients.map((c) => (
                      <option key={c.client_id} value={c.client_id}>
                        {c.name}
                      </option>
                    ))}
                </select>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('forge.wizard.forge', 'Forge')}
              </label>
              {lockedOverview ? (
                <div className={cn(inputCls, 'flex items-center gap-2 text-foreground/80')}>
                  {resolving && !resolvedForge ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                      <span className="text-muted-foreground">
                        {t('forge.wizard.loading', 'Loading…')}
                      </span>
                    </>
                  ) : (
                    resolvedForgeName || '-'
                  )}
                </div>
              ) : (
                <select
                  className={inputCls}
                  value={overview.forge_id}
                  onChange={(e) => setOverview({ ...overview, forge_id: e.target.value })}
                  disabled={!overview.client_id}
                >
                  <option value="">
                    {overview.client_id
                      ? t('forgeDashboard.applications.selectForge', '-- Select a forge --')
                      : t('forgeDashboard.applications.pickClientFirst', 'Pick a client first')}
                  </option>
                  {forgesForSelectedClient.map((f) => (
                    <option key={f.forge_id} value={f.forge_id}>
                      {f.display_name || f.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              {t('forge.wizard.name', 'Application name')}
            </label>
            <input
              className={inputCls}
              value={overview.name}
              onChange={(e) => setOverview({ ...overview, name: e.target.value })}
              placeholder="my-app"
              disabled={lockedOverview}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('forge.wizard.appNameHint', 'lowercase, digits, hyphens (2–46)')}
            </p>
            {!lockedOverview && overview.name.length > 0 && !NAME_PATTERN.test(overview.name) && (
              <p className="text-xs text-destructive mt-1">
                {t('forge.wizard.nameInvalid', 'Invalid name. Use lowercase letters, digits, and hyphens.')}
              </p>
            )}
            {nameTaken && (
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <p className="text-xs text-destructive">
                  {t(
                    'forge.wizard.nameTaken',
                    'An application named "{{name}}" already exists in this forge.',
                    { name: overview.name },
                  )}
                </p>
                {suggestedName && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => setOverview((prev) => ({ ...prev, name: suggestedName }))}
                  >
                    {t('forge.wizard.useSuggestedName', 'Use "{{name}}"', { name: suggestedName })}
                  </Button>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              {t('forge.wizard.description', 'Description')}
            </label>
            <textarea
              className={inputCls}
              rows={3}
              value={overview.description}
              onChange={(e) => setOverview({ ...overview, description: e.target.value })}
              disabled={lockedOverview}
              placeholder={t('forge.wizard.descriptionPlaceholder', 'Short description of the application')}
            />
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-3">
          {templatesLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              {t('forge.wizard.loadingTemplates', 'Loading templates…')}
            </div>
          ) : templatesError ? (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-destructive text-sm">
              {templatesError}
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              {t('forge.wizard.noTemplates', 'No templates available')}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {templates.map((tpl) => {
                const isSelected = selectedTemplate?.template_name === tpl.template_name;
                const portsEntries = Object.entries(tpl.default_ports || {});
                return (
                  <button
                    key={tpl.template_name}
                    type="button"
                    onClick={() => setSelectedTemplate(tpl)}
                    className={cn(
                      'text-left rounded-xl border p-4 transition-all hover:border-primary/60 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      isSelected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border bg-card',
                    )}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Layers className={cn('h-4 w-4 flex-shrink-0', isSelected ? 'text-primary' : 'text-muted-foreground')} />
                        <h4 className="font-semibold text-sm text-foreground truncate">
                          {tpl.display_name}
                        </h4>
                      </div>
                      {isSelected && (
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground flex-shrink-0">
                          <Check className="h-3 w-3" />
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                      {tpl.description}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="inline-flex items-center gap-1 rounded-md bg-muted/60 px-2 py-0.5 text-[10px] text-muted-foreground">
                        <Box className="h-3 w-3" />
                        {tpl.app_type}
                      </span>
                      {tpl.has_backend && (
                        <span className="inline-flex items-center gap-1 rounded-md bg-blue-500/10 px-2 py-0.5 text-[10px] text-blue-600 dark:text-blue-400">
                          <Server className="h-3 w-3" />
                          {t('forge.wizard.backend', 'Backend')}
                        </span>
                      )}
                      {tpl.has_frontend && (
                        <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-600 dark:text-emerald-400">
                          <Globe className="h-3 w-3" />
                          {t('forge.wizard.frontend', 'Frontend')}
                        </span>
                      )}
                      {portsEntries.map(([k, v]) => (
                        <span
                          key={k}
                          className="inline-flex items-center rounded-md bg-muted/60 px-2 py-0.5 text-[10px] text-muted-foreground"
                          title={t('forge.wizard.defaultPort', 'Default port')}
                        >
                          {k}:{v}
                        </span>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-3">
            <h4 className="text-sm font-semibold text-foreground">
              {t('forge.wizard.summary.title', 'Review')}
            </h4>
            <dl className="grid grid-cols-3 gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted-foreground">{t('forge.wizard.name', 'Application name')}</dt>
              <dd className="col-span-2 font-medium text-foreground">{overview.name}</dd>

              <dt className="text-muted-foreground">{t('forge.wizard.client', 'Client')}</dt>
              <dd className="col-span-2 text-foreground/90">{resolvedClientName}</dd>

              <dt className="text-muted-foreground">{t('forge.wizard.forge', 'Forge')}</dt>
              <dd className="col-span-2 text-foreground/90">{resolvedForgeName}</dd>

              <dt className="text-muted-foreground">{t('forge.wizard.template', 'Template')}</dt>
              <dd className="col-span-2 text-foreground/90">
                {selectedTemplate ? (
                  <>
                    {selectedTemplate.display_name}
                    <span className="text-xs text-muted-foreground ml-2">
                      ({selectedTemplate.template_name})
                    </span>
                  </>
                ) : (
                  '-'
                )}
              </dd>

              <dt className="text-muted-foreground">{t('forge.wizard.appType', 'App type')}</dt>
              <dd className="col-span-2 text-foreground/90">{selectedTemplate?.app_type || '-'}</dd>

              {overview.description && (
                <>
                  <dt className="text-muted-foreground">{t('forge.wizard.description', 'Description')}</dt>
                  <dd className="col-span-2 text-foreground/90 whitespace-pre-wrap">
                    {overview.description}
                  </dd>
                </>
              )}
            </dl>
          </div>
          <p className="text-xs text-muted-foreground">
            {t(
              'forge.wizard.summary.help',
              'A new application will be provisioned in Forge using the selected template. This can take a minute.',
            )}
          </p>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-muted/20 p-4">
            <p className="text-sm font-medium text-foreground mb-2">
              {t('forge.progress.buildInProgress', 'Build in progress...')}
            </p>
            <ProgressStepper
              steps={createSteps}
              currentStep={createProgressStep >= 0 ? createProgressStep : 0}
              hasError={createProgressError}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {t(
              'forge.wizard.provisioningHelp',
              'Creating and preparing your application in Forge. This can take a moment.',
            )}
          </p>
        </div>
      )}
    </Modal>
  );
};

export default ApplicationCreationWizard;
